import React, { useEffect, useMemo, useState } from 'react'
import ProfileWrapper from '../ProfileWrapper'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button, Switch, TextField, Tooltip } from '@material-ui/core'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import CloseIcon from '@material-ui/icons/Close'
import { useTheme } from '@material-ui/core/styles'
import config from '../../../config'
import { convertJSONToObject, switchStyles, toastError, toastSuccess, useAutoCompleteStyles } from '../../../utils'
import style from './style.module.sass'
import { useActions } from '../../../utils/action-helper'
import { userGetAllowLists, userGetTextTone, userSaveOption, userSetOption } from '../../../actions'
import TLServiceComponent from '../../../contexts/TLServiceComponent'
import { ToastContainer } from 'react-toastify'
import { Redirect } from 'react-router-dom'


const Notifications = () => {
    const { t, i18n: { language: i18nLanguage } } = useTranslation()
    const { theme: { bgColors, borderColors, colors } } = useTheme()
    const tlService = React.useContext(TLServiceComponent)
    const themeState = useSelector(state => state.userData.theme || localStorage.getItem('theme') || config.THEMES_MODES.LIGHT)
    const token = useSelector((state) => state.userData.token)
    const userId = useSelector((state) => state.userData.user.id)
    const userType = useSelector(state => state.userData.user.user_type_id)
    const isContrast = themeState !== config.THEMES_MODES.LIGHT
    const autoCompleteClasses = useAutoCompleteStyles(bgColors, colors)()
    const switchClasses = switchStyles(bgColors, colors)()
    const collectiveBasinReportRaw = useSelector(state => state.userData.user?.user_options?.collective_basin_report)
    const allowedListsData = useSelector(state => state.userData.allowedLists) || []
    const textToneData = useSelector(state => state.userData.textTone) || []
    const { actionUserGetAllowLists, actionUserGetTextTone, actionUserSetOption, actionUserSaveOption } = useActions({
        actionUserGetAllowLists: userGetAllowLists(tlService),
        actionUserGetTextTone: userGetTextTone(tlService),
        actionUserSetOption: userSetOption,
        actionUserSaveOption: userSaveOption(tlService),
    })
    const [editedReport, setEditedReport] = useState(() => convertJSONToObject(collectiveBasinReportRaw, config.defaultColBasinReport))

    useEffect(() => {
        if (token) {
            actionUserGetAllowLists(token)
            actionUserGetTextTone(token)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    useEffect(() => {
        setEditedReport(convertJSONToObject(collectiveBasinReportRaw, config.defaultColBasinReport))
    }, [collectiveBasinReportRaw])

    const allowlistOptions = useMemo(
        () =>
            allowedListsData.map((el) => ({
                value: el.id,
                label: `(${el.id}) ${el.name?.[i18nLanguage]}`,
            })),
        [allowedListsData, i18nLanguage]
    )

    const textbinOptions = useMemo(
        () =>
            textToneData.map((el) => ({
                value: el.id,
                label: `(${el.id}) ${el.name?.[i18nLanguage] || ''}`.trim(),
            })),
        [textToneData, i18nLanguage]
    )
    const sectionConfig = [
        {
            key: 'allowlist',
            title: 'profile_allowlist_notifications',
            options: allowlistOptions,
        },
        {
            key: 'textbin',
            title: 'profile_textbin_notifications',
            options: textbinOptions,
        },
    ]

    const getRowState = (sectionKey, rowKey) => {
        const section = editedReport?.[sectionKey] || {}
        const row = section?.[rowKey] || {}
        return { ...config.defaultRowStateColBasin, ...row, ids: row?.ids || [] }
    }

    const updateRow = (sectionKey, rowKey, change) => {
        setEditedReport(prev => {
            const section = prev?.[sectionKey] || {}
            const current = { ...config.defaultRowStateColBasin, ...section[rowKey] }
            const next = typeof change === 'function' ? change(current) : { ...current, ...change }
            return {
                ...prev,
                [sectionKey]: { ...section, [rowKey]: next },
            }
        })
    }

    const handleToggle = (sectionKey, rowKey, field) => (_, checked) => {
        updateRow(sectionKey, rowKey, row => ({ ...row, [field]: checked }))
    }

    const handleIdsChange = (sectionKey, rowKey) => (_, newValue) => {
        const ids = (newValue || []).map(opt => opt.value)
        updateRow(sectionKey, rowKey, row => ({ ...row, ids }))
    }

    const isTermListSelectionValid = () => {
        return sectionConfig.every(section =>
            config.colBasinRowOrder.every(row => {
                const rowState = getRowState(section.key, row.key)
                if (!rowState.enabled) return true
                if (rowState.all) return true
                return Array.isArray(rowState.ids) && rowState.ids.length > 0
            })
        )
    }

    const mapSelectedOptions = (options, ids = []) => {
        const idsAsString = ids.map(String)
        const baseOptions = options.filter((opt) => idsAsString.includes(String(opt.value)))
        const missing = ids
            .filter((id) => !options.some((opt) => String(opt.value) === String(id)))
            .map((id) => ({ value: id, label: String(id) }))
        return [...baseOptions, ...missing]
    }

    const handleSaveColBasinReport = () => {
        if (!isTermListSelectionValid()) {
            toastError(t('profile_notifications-select-error-msg'), bgColors, colors)
            return
        }
        actionUserSetOption('collective_basin_report', JSON.stringify(editedReport),)
        actionUserSaveOption(token, userId, 'collective_basin_report', JSON.stringify(editedReport))
        toastSuccess(t('profile_notification_success-msg'), bgColors, colors)
    }

    if (userType !== 1 && userType !== 2) {
        return <Redirect to={'/'} />
    }
    return (
        <ProfileWrapper>
            <ToastContainer autoClose={5000} position="top-center" hideProgressBar={false} draggable pauseOnHover closeOnClick />
            <div className={style.container} style={{ borderTop: `2px solid ${borderColors.borderPrimary1000}`}}>
                <div className={style.actions}>
                    <Button variant="contained" onClick={handleSaveColBasinReport} style={{ backgroundColor: bgColors.bgBlue1000, color: colors.colorContrast, marginTop: '10px' }}> {t('Save')}</Button>
                </div>
                {sectionConfig.map((section) => (
                    <div
                        key={section.key}
                        className={style.section}
                        style={{ borderColor: borderColors.borderPrimary1000, backgroundColor: themeState === config.THEMES_MODES.CONTRAST_LIGHT ? bgColors.bgPrimary200 : bgColors.bgPrimary0 }}
                    >
                        <div className={style.sectionHeader} style={{ color: colors.colorPrimary500 }}>
                            {t(section.title)}
                        </div>
                        <div className={style.headerRow} style={{ color: isContrast ? colors.colorText : colors.colorMutedText }}>
                            <span />
                            <span className={style.columnLabel}>{t('profile_notifications_enable')}</span>
                            <span className={style.columnLabel}>{t('profile_notifications_email')}</span>
                            <span className={style.columnLabel}>{t('profile_notifications_all')}</span>
                            <span className={style.columnLabel}>{t('profile_notifications_term_categories')}</span>
                        </div>
                        <div className={style.rows}>
                            {config.colBasinRowOrder.map((row) => {
                                const rowState = getRowState(section.key, row.key)
                                const options = section.options || []
                                const selected = mapSelectedOptions(options, rowState.ids)
                                const disabledSelect = !rowState.enabled || rowState.all
                                const tooltipTitle = disabledSelect ? (!rowState.enabled ? t('profile_notifications_enabled-disabled-tooltip') : t('profile_notifications_select-disabled-tooltip')) : ''
                                return (
                                    <div
                                        key={row.key}
                                        className={style.row}
                                        style={{
                                            backgroundColor: bgColors.bgPrimary0,
                                            boxShadow: `0 0 6px ${borderColors.borderPrimary100}`,
                                            borderColor: borderColors.borderPrimary100,
                                        }}
                                    >
                                        <div className={style.rowLabel} style={{ color: colors.colorText }}>
                                            {t(row.label)}
                                        </div>
                                        <div className={style.cell}>
                                            <span className={style.cellLabel} style={{ color: colors.colorMutedText }}>
                                                {t('profile_notifications_enable')}
                                            </span>
                                            <Switch
                                                checked={!!rowState.enabled}
                                                onChange={handleToggle(section.key, row.key, 'enabled')}
                                                color="primary"
                                                className={isContrast ? switchClasses.root : ''}
                                            />
                                        </div>
                                        <div className={style.cell}>
                                            <span className={style.cellLabel} style={{ color: colors.colorMutedText }}>
                                                {t('profile_notifications_email')}
                                            </span>
                                            <Switch
                                                checked={!!rowState.email}
                                                onChange={handleToggle(section.key, row.key, 'email')}
                                                color="primary"
                                                disabled={!!rowState.enabled === false}
                                                className={isContrast ? switchClasses.root : ''}
                                            />
                                        </div>
                                        <div className={style.cell}>
                                            <span className={style.cellLabel} style={{ color: colors.colorMutedText }}>
                                                {t('profile_notifications_all')}
                                            </span>
                                            <Switch
                                                checked={!!rowState.all}
                                                onChange={handleToggle(section.key, row.key, 'all')}
                                                color="primary"
                                                disabled={!!rowState.enabled === false}
                                                className={isContrast ? switchClasses.root : ''}
                                            />
                                        </div>
                                        <div className={`${style.cell} ${style.selectCell}`}>
                                            <span className={style.cellLabel} style={{ color: colors.colorMutedText }}>
                                                {t('profile_notifications_term_categories')}
                                            </span>
                                            <Tooltip title={tooltipTitle} arrow placement='top-end'>
                                            <Autocomplete
                                                multiple
                                                disableCloseOnSelect
                                                options={options}
                                                value={selected}
                                                getOptionLabel={(option) => option?.label || ''}
                                                onChange={handleIdsChange(section.key, row.key)}
                                                disabled={!!disabledSelect}
                                                style={{ width: '100%', backgroundColor: bgColors.bgPrimary200, borderRadius: '10px' }}
                                                classes={{
                                                    paper: isContrast ? autoCompleteClasses.paper : '',
                                                    option: isContrast ? autoCompleteClasses.option : '',
                                                    clearIndicator: isContrast ? autoCompleteClasses.clearIndicator : '',
                                                    popupIndicator: isContrast ? autoCompleteClasses.popupIndicator : '',
                                                    noOptions: isContrast ? autoCompleteClasses.noOptions : '',
                                                    inputRoot: isContrast ? autoCompleteClasses.inputRoot : '',
                                                }}
                                                slotProps={{
                                                    paper: {
                                                        style: {
                                                            backgroundColor: bgColors.bgBoxes,
                                                            color: colors.colorText,
                                                        },
                                                    },
                                                    clearIndicator: {
                                                        style: {
                                                            color: colors.colorText,
                                                        },
                                                    },
                                                    popupIndicator: {
                                                        style: {
                                                            color: colors.colorText,
                                                        },
                                                    },
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder={t('Select')}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            style: {
                                                                color: colors.colorText,
                                                                backgroundColor: bgColors.bgPrimary200,
                                                                borderRadius: 10,
                                                            },
                                                            classes: { input: style.placeholderInput },
                                                        }}
                                                        InputLabelProps={{
                                                            style: { color: colors.colorText },
                                                        }}
                                                    />)}
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            {...getTagProps({ index })}
                                                            key={option.value}
                                                            label={option.label}
                                                            size="medium"
                                                            deleteIcon={<CloseIcon style={{ color: colors.colorText }} />}
                                                            style={{ backgroundColor: bgColors.bgPrimary100, color: colors.colorText }}
                                                        />
                                                    ))
                                                }
                                            />
                                            </Tooltip>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </ProfileWrapper>
    )
}

export default Notifications
